import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from '../_library/intl';
import { Button, Checkbox, Filter, Form, Icon, InputSelect, Loader, Modal, Text, Calendar, Dropdown } from './';
import { api, formatDate, formatNumber, useAppSelector } from '../_library';
import config from "../config";
import * as Yup from 'yup';
import {FormikValues} from "formik/dist/types";

type Props = {
    columns: {
        name: string,
        label: string,
        type?: 'rows' | 'status' | 'role' | 'date' | 'number' | 'money' | 'percents' | 'confirmation' | 'textListByLangs' | 'receipt',
        actions?: {
            hideWhen?: string,
            hideWhenValue?: any,
            label: string,
            onClick: (row: any, columnRow: any) => void
        }[],
        rows?: string[],
        includeIndex?: boolean,
        width?: string,
        icons?: React.ReactNode[],
        withTime?: boolean,
        descriptionField?: string,
        statuses?: { [key: string]: string | [string, string] },
        className?: string,
        tooltip?: React.ReactNode,
        tooltipKey?: React.ReactNode,
        blockInfoKey?: string,
        render?: (row: any) => React.ReactNode;
    }[],
    dataLoader?: {
        apiCall?: {
            path: string,
            rowsNameInResponse: string,
            totalPagesNameInResponse: string,
            totalRecordsNameInResponse: string,
            totalSumNameInResponse?: string,
            dateLimit?: string,
            downloadDataPath?: string,
            downloadedFileName?: string,
            downloadFileCreationInBackground?: boolean,
            downloadFileCreationInBackgroundTitle?: string
            downloadFileCreationInBackgroundDescription?: string,
            downloadFileCreationInBackgroundDescriptionNoTimeRemainingAvailable?: string,
            downloadFileAvailableCheckPath?: string,
            downloadFileCreatedInBackgroundPath?: string,
            downloadInBackgroundLabelValues?: any
            additionalParam?: string,
        }
    },
    rows?: {[key: string]: any}[],
    total?: number,  //total records
    sum?: number, //total sum of one of column, for example sum of all transactions for all pages
    pages?: number,  //total pages
    setPage?: (page: number) => void,
    page?: number,
    limit?: number
    pagination?: boolean,
    title?: string,
    periodSelect?: boolean,
    periodIncludeAll?: boolean,
    periodInclude18Months?: boolean,
    itemsPerPage?: number,
    itemsPerPageSelect?: boolean | {
        value: number,
        options: { value: number; label: string }[],
        onChange: (value: number) => void,
    },
    dateSelect?: boolean,
    exportDataButton?: boolean
    statuses?: {[key: string]: string},
    roles?: {[key: string]: string},
    actions?: {
        onClick: (row: any, idx?: number) => void,
        label: string,
        secondLabel?: string,
        showSecondLabelWhen?: string,
        showSecondLabelWhenValue?: any,
        hideWhen?: string,
        hideWhenValue?: any,
        color?: string,
        icon?: string
    }[],
    renderActionButtons?: (isLoading: boolean, downloadHandler: () => void) => React.ReactNode[],
    onRowSelected?: (rows: any[], row?: any) => void, //display checkbox on each row and allow to select row or not
    onRowSelectPassDataField?: string,  //only one field from row will be passed to onRowSelected if not passed then whole row will be passed
    isSearchEnabled?: boolean,
    renderActionButtonsForSelectedRows?: (isLoading: boolean) => React.ReactNode[],
    showSelectedActionsDivider?: boolean,
    filter?: {
        btnLabel: string,
        fields: {type: string, name: string, label: string, options?: {label: string, value: string}[],
          inputPlaceholder?: string, inputLabel?: string, inputOptions?: {label?: string, display: string, value: string}[],
          inputNotLabels?: boolean,
        }[],
        filterFullHeight?: boolean
    }
    setPeriod?: (period: string) => void,
    setFilterRef?: (ref: any) => void,
    setSearchRef?: (ref: any) => void,
    setCalendarRef?: (value: Value | null) => void,
    reloadDataTrigger?: (dataLoader: any) => void,
    clearSelectionTrigger?: (clearSelection: () => void) => void,
    filterPanelPosition?: 'left' | 'right',
    filterPanelWidthClassName?: string,
    additionalData?: any,
    onAdditionalDataLoad?: (data: any[]) => void,
    onSummaryLoad?: (summary: any) => void
};

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];


export const Table: (props: Props) => JSX.Element =
    ({
         columns,
         dataLoader,
         rows,
         total,
         sum,
         pages,
         setPage: setPageCallback,
         page: pageProvided,
         pagination = true,
         limit,
         title,
         periodSelect,
         periodIncludeAll,
         periodInclude18Months,
         itemsPerPageSelect,
         dateSelect,
         exportDataButton,
         actions,
         statuses,
         roles,
         renderActionButtons,
         onRowSelected,
         onRowSelectPassDataField,
         isSearchEnabled,
         renderActionButtonsForSelectedRows,
         showSelectedActionsDivider = false,
         filter,
         setPeriod,
         setFilterRef,
         setSearchRef,
         setCalendarRef,
         reloadDataTrigger,
         clearSelectionTrigger,
         filterPanelPosition,
         filterPanelWidthClassName,
         additionalData,
         onAdditionalDataLoad,
         onSummaryLoad
     }) => {

        const { lang } = useAppSelector(state => state.i18n);
        const { currency } = useAppSelector(state => state.user);
        const [isLoading, setIsLoading] = useState(!rows);
        const [rowsData, setRowsData] = useState((rows ? rows : []));
        const [totalPages, setTotalPages] = useState((pages ? pages : 0));
        const [totalRecords, setTotalRecords] = useState((total ? total : rows ? rows.length : 0));
        const [totalSum, setTotalSum] = useState((sum ? sum : 0));
        const page = useRef((pageProvided ? pageProvided : 1));
        const [selectedRows, setSelectedRows] = useState<number[]>([]);
        const [search, setSearch] = useState('');
        const [dateLimit, setDateLimit] = useState('1y');
        const searchRef = useRef('');
        const [itemsPerPage, setItemsPerPage] = useState('');
        const { itemsPerPage: itemsPerPageFromSettings } = useAppSelector(state => state.user);
        const [calendarValue, setCalendarValue] = useState<Value | null>(null);
        const filterDataRef = useRef('');
        const [confirmationModalContent, setConfirmationModalContent] = useState<string | null>(null);
        const [shouldResetCalendar, setShouldResetCalendar] = useState(false);
        const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
        const [downloadFileInBackgroundCreationModalIsOpen, setDownloadFileInBackgroundCreationModalIsOpen] = useState(false);
        const [timeRemainingTillFileReadyForDownload, setTimeRemainingTillFileReadyForDownload] = useState<null | number>(null);  // rime remaining till file is ready for download in seconds
        const downloadFileInterval = useRef<any>(null);
        const [fileGenerationInProgressId, setFileGenerationInProgressId] = useState<null | number | string>(null);
        const [fileDownloadSuccess, setFileDownloadSuccess] = useState(false);
        const [fileDownloadFailed, setFileDownloadFailed] = useState(false);
        const timeRemainingTillFileReadyForDownloadRef = useRef<any>(null);
        const [downloadInBackgroundLabelValues, setDownloadInBackgroundLabelValues] = useState<any>(null);
        const intl = useIntl();
        const test = useRef<any>(null);

        const handleCloseDownloadFileInBackgroundCreationModal = () => {
            setDownloadFileInBackgroundCreationModalIsOpen(false);
            setFileDownloadSuccess(false);
            setFileDownloadFailed(false);
            setFileGenerationInProgressId(null);
            setTimeRemainingTillFileReadyForDownload(null);
            timeRemainingTillFileReadyForDownloadRef.current = null;
            setDownloadInBackgroundLabelValues(null);
            setIsDownloadInProgress(false);
        };

        const handleSetPage = (pageNumber: number, callLoadData:boolean=true) => {
            if (setPageCallback) {
                setPageCallback(pageNumber);
            }

            page.current = pageNumber;
            if (callLoadData) {
                loadData();
            }
        };

        const loadData = (filter?: string, silent: boolean = false) => {
            // silent=true: background refresh without showing loading state and
            // without clearing the user's row selection. Used by auto-poll so the
            // table doesn't flicker when nothing changes.
            if (!silent) {
                setIsLoading(true);
            }
            const time = new Date();

            if (dataLoader?.apiCall) {
                test.current = time;

                //NOTE: axios removes empty arrays from get params if passed
                api.get(`${dataLoader.apiCall.path}?page=${page.current}${search ? `&search=${search}` : ''}${filter ? `&filter=${filter}` : ''}${dataLoader.apiCall.dateLimit ? `&dateLimit=${dataLoader.apiCall.dateLimit}` : `&dateLimit=${dateLimit}`}${calendarValue && Array.isArray(calendarValue) ? `&dateFrom=${formatDate(calendarValue[0])}&dateTo=${formatDate(calendarValue[1])}` : ''}${filterDataRef.current ? `&filter=${filterDataRef.current}` : ''}${itemsPerPage ? `&itemsPerPage=${itemsPerPage}` : `&itemsPerPage=${itemsPerPageFromSettings}`}${dataLoader.apiCall.additionalParam ? `&${dataLoader.apiCall.additionalParam}` : ''}`)
                    .then(response => {
                        if (test.current === time) {
                            if (!silent) {
                                setSelectedRows([]);
                                if (onRowSelected) {
                                    onRowSelected([]);
                                }
                            }
                            setRowsData(response.data[dataLoader.apiCall!!.rowsNameInResponse]);
                            setTotalPages(response.data[dataLoader.apiCall!!.totalPagesNameInResponse]);
                            setTotalRecords(response.data[dataLoader.apiCall!!.totalRecordsNameInResponse])
                            if (dataLoader.apiCall!!.totalSumNameInResponse !== undefined && response.data[dataLoader.apiCall!!.totalSumNameInResponse] !== undefined) {
                                setTotalSum(response.data[dataLoader.apiCall!!.totalSumNameInResponse])
                            }
                            if (additionalData !== undefined
                                && response.data[additionalData]
                                && onAdditionalDataLoad !== undefined) {
                                    onAdditionalDataLoad(response.data[additionalData])
                            }
                            if (response.data.receiptSummary && onSummaryLoad) {
                                onSummaryLoad(response.data.receiptSummary);
                            }
                            if (!silent) {
                                setIsLoading(false);
                            }
                        }
                    })
                    .catch()
            } else {
              if (!silent) setIsLoading(false);
            }
        };

        const handleDataDownload = () => {
            setIsDownloadInProgress(true);
            if (dataLoader?.apiCall) {
                const isBackgroundDownload = Boolean(dataLoader.apiCall.downloadFileCreationInBackground);
                api.get(`${dataLoader.apiCall.downloadDataPath}?${search ? `search=${search}` : ''}${filterDataRef.current ? `${filterDataRef.current}` : ''}${calendarValue && Array.isArray(calendarValue) ? `&dateFrom=${formatDate(calendarValue[0])}&dateTo=${formatDate(calendarValue[1])}` : ''}&lang=${lang}${dataLoader.apiCall.additionalParam ? `&${dataLoader.apiCall.additionalParam}` : ''}`,
                        isBackgroundDownload ? undefined : { responseType: 'arraybuffer' })
                .then(res => {
                    setIsDownloadInProgress(false);

                    if (isBackgroundDownload) {
                      const responseData = res.data || {};

                      if (responseData.timeRemaining !== undefined && responseData.timeRemaining !== null) {
                        setTimeRemainingTillFileReadyForDownload(responseData.timeRemaining);
                        timeRemainingTillFileReadyForDownloadRef.current = responseData.timeRemaining;
                      }

                      if (responseData.fileId !== undefined && responseData.fileId !== null) {
                        setFileGenerationInProgressId(responseData.fileId);
                      }

                      setDownloadInBackgroundLabelValues(
                        responseData.descriptionValues || dataLoader.apiCall?.downloadInBackgroundLabelValues || null
                      );

                      setDownloadFileInBackgroundCreationModalIsOpen(true);

                      return;
                    }

                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
                    const link = document.createElement('a');

                    link.href = url;
                    if (dataLoader.apiCall?.downloadedFileName) {
                        link.setAttribute('download', dataLoader.apiCall.downloadedFileName);
                    } else {
                        link.setAttribute('download', 'records.csv');
                    }
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                }).catch(res => {
                  setIsDownloadInProgress(false);
                });
            }
        };

        const handleFilterSubmit = (values: FormikValues) => {
            handleSetPage(1, false);

            let data = '';

            Object.keys(values).forEach((key) => {
                if (key !== 'search') {
                    data += `&${key}=${values[key]}`;
                } else {
                    data += `&search=${values[key]}`;
                }
            });

            if (data !== filterDataRef.current || (values.search !== undefined && values.search !== search)) {
                if (values.search !== undefined && values.search !== search) {
                    setSearch(values.search);
                }

                filterDataRef.current = data;
                setFilterRef?.(filterDataRef);
                loadData(data);
            }
        };

        const resetFilter = () => {
            filterDataRef.current = '';
            searchRef.current = '';
            loadData('');
        };

        const onCalendarChange = (newValue: Value) => {
            setCalendarValue(newValue);
            setShouldResetCalendar(false);
            setCalendarRef?.(newValue);
        };

        const onCalendarReset = () => {
            setCalendarValue(null);
            setShouldResetCalendar(true);
            setCalendarRef?.(null);
        };

        useEffect(() => {
          // if search reset
          if (search === '') {
            loadData('');
          }
        }, [search]);

        useEffect(() => {
          loadData('');
        }, [dateLimit]);

        useEffect(() => {
          if (shouldResetCalendar) {
            loadData('');
          }
        }, [shouldResetCalendar]);

        useEffect(() => {
            if (itemsPerPage !== '') {
                loadData('');
            }
        }, [itemsPerPage]);

      useEffect(() => {
        // @ts-ignore
        setRowsData(rows);
      }, [rows]);

        useEffect(() => {
          if (timeRemainingTillFileReadyForDownload !== null && downloadFileInterval.current == null) {
            downloadFileInterval.current = setInterval(() => {
              if (timeRemainingTillFileReadyForDownloadRef.current > 0) {
                setTimeRemainingTillFileReadyForDownload(timeRemainingTillFileReadyForDownloadRef.current - 1);
                timeRemainingTillFileReadyForDownloadRef.current -= 1;

                //call backend to check file status
                if (dataLoader?.apiCall?.downloadFileAvailableCheckPath && fileGenerationInProgressId !== null) {
                  api.get(`${dataLoader?.apiCall?.downloadFileAvailableCheckPath.replace('FILE_ID', `${fileGenerationInProgressId}`)}`)
                    .then((res) => {
                      // Worker reported failure: stop polling and surface error to user.
                      if (res.data?.failed) {
                        clearInterval(downloadFileInterval.current);
                        setTimeRemainingTillFileReadyForDownload(null);
                        timeRemainingTillFileReadyForDownloadRef.current = null;
                        setIsDownloadInProgress(false);
                        setFileDownloadFailed(true);
                        return;
                      }
                      if (res.data.available) {
                        //terminate interval, show that download starting, call backend to get file
                        clearInterval(downloadFileInterval.current);
                        setIsDownloadInProgress(true);
                        setTimeRemainingTillFileReadyForDownload(null);
                        timeRemainingTillFileReadyForDownloadRef.current = null;
                        setDownloadInBackgroundLabelValues(null);

                        if (dataLoader?.apiCall?.downloadFileCreatedInBackgroundPath) {
                          api.get(`${dataLoader?.apiCall?.downloadFileCreatedInBackgroundPath.replace('FILE_ID', `${fileGenerationInProgressId}`)}`, {responseType: 'blob'})
                            .then(res => {
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const link = document.createElement('a');

                              link.href = url;
                              if (dataLoader.apiCall?.downloadedFileName) {
                                link.setAttribute('download', dataLoader.apiCall.downloadedFileName);
                              } else {
                                link.setAttribute('download', 'records.csv');
                              }
                              document.body.appendChild(link);
                              link.click();
                              link.remove();

                              window.URL.revokeObjectURL(url);
                              setFileDownloadSuccess(true);
                              setIsDownloadInProgress(false);
                            }).catch(res => {
                              setIsDownloadInProgress(false);
                            });
                        }
                      }
                    })
                    .catch(() => {});
                }

              } else {
                clearInterval(downloadFileInterval.current);
              }
            }, 1000);
          }
        }, [timeRemainingTillFileReadyForDownload]);

        useEffect(() => {
            if (reloadDataTrigger) {
                reloadDataTrigger(loadData);
            }

            if (clearSelectionTrigger) {
                clearSelectionTrigger(() => {
                    setSelectedRows([]);
                    if (onRowSelected) {
                        onRowSelected([]);
                    }
                });
            }

            loadData();

            return () => {
              if (downloadFileInterval.current !== null) {
                clearInterval(downloadFileInterval.current);
              }
            }

        }, []);

        useEffect(() => {
            if (typeof itemsPerPageSelect === 'object' && itemsPerPageSelect.value) {
                setItemsPerPage(String(itemsPerPageSelect.value));
            }
        }, [itemsPerPageSelect]);

        const renderHeader = () => (
            <div>
                <div className="lg:flex justify-between min-h-[50px] items-center mb-8 lg:space-y-0 space-y-6">
                    <div>
                        {title ?
                            <Text className="font-bold" twoXL={true} label={title} />
                            : null
                        }
                    </div>
                    <div className="lg:flex lg:space-x-2 space-x-0 lg:space-y-0 space-y-4">
                        {isSearchEnabled ?
                            <div className="flex items-end">
                                <Form
                                    enableReinitialize={true}
                                    initValues={{search}}
                                    validationSchema={{search: Yup.string()}}
                                    noSubmitBtn={true}
                                    onSubmit={() => loadData()}
                                    onChange={({search: searchValue}: {search: string}) => {
                                        if (setPageCallback) {
                                            setPageCallback(1);
                                        }

                                        page.current = 1;

                                        searchRef.current = `&search=${searchValue}`;
                                        setSearchRef?.(searchRef);

                                        setSearch(searchValue);
                                    }}
                                    className="!space-y-0"
                                    fieldsWrapperClassName="!space-y-0"
                                    fields={[
                                        {
                                            name: 'search',
                                            type: 'text',
                                            placeholder: 'bk.table.placeholder.search_users',
                                            inputClassName: 'h-14 border-t-0 border-l-0 border-r-0 rounded-none focus:outline-none focus:ring-0 focus:ring-transparent focus:border-bk-grey-light !placeholder-bk-black !text-md',
                                            icon: <Icon type="search" />
                                        }
                                    ]}
                                />
                            </div>
                            : null
                        }
                        {filter ?
                            <Filter
                                disabled={isLoading}
                                btnLabel={filter.btnLabel}
                                filters={filter.fields}
                                onFilterUpdate={handleFilterSubmit}
                                resetFilter={resetFilter}
                                search={search}
                                fullHeight={filter.filterFullHeight}
                                panelPosition={filterPanelPosition}
                                panelWidthClassName={filterPanelWidthClassName}
                            />
                            : null
                        }
                        {itemsPerPageSelect ?
                            <InputSelect
                                disabled={isLoading}
                                notInForm={true}
                                options={[
                                    {name: intl.formatMessage({ id: 'bk.table.items_per_page' }, { page: 10 }), value: '10'},
                                    {name: intl.formatMessage({ id: 'bk.table.items_per_page' }, { page: 25 }), value: '25'},
                                    {name: intl.formatMessage({ id: 'bk.table.items_per_page' }, { page: 50 }), value: '50'},
                                    {name: intl.formatMessage({ id: 'bk.table.items_per_page' }, { page: 100 }), value: '100'},
                                ]}
                                selected={`${itemsPerPageFromSettings ?? 10}`}
                                onChange={(value: any) :void => {setItemsPerPage(value)}}
                                noInput={true}
                            />
                            : null
                        }
                        {exportDataButton ?
                            <Button
                                disabled={isLoading}
                                color="transparent"
                                label="bk.dashboard.last_transactions.btn.download_csv"
                                onClick={() => handleDataDownload()}
                                isLoading={isDownloadInProgress}
                                icon={<Icon type="download" />}
                                smallPaddingOnSides={true}
                                noShadow={true}
                            />
                            : null
                        }
                        {renderActionButtons ?
                            <>{renderActionButtons(isLoading, handleDataDownload)}</>
                            : null
                        }
                        {periodSelect ?
                            <InputSelect
                                disabled={isLoading}
                                notInForm={true}
                                options={[
                                    ...(periodIncludeAll ? [{name: intl.formatMessage({id: 'bk.common.all'}), value: 'all'}] : []),
                                    ...(periodInclude18Months ? [{name: '18 months', value: '1y6m'}] : []),
                                    {name: '12 months', value: '1y'},
                                    {name: '6 months', value: '6m'},
                                    {name: '3 months', value: '3m'},
                                    {name: '1 month', value: '1m'}
                                ]}
                                selected="1y"
                                onChange={(value: any) => {setDateLimit(value); setPeriod?.(value);}}
                                noInput={true}
                            />
                            : null
                        }
                        {dateSelect ?
                            <>
                                <Dropdown
                                    smallContainer={true}
                                    btnLabel={"bk.table.select_date"}
                                    content={
                                        <>
                                            <div>
                                                <Calendar
                                                    value={calendarValue}
                                                    onChange={onCalendarChange}
                                                />
                                            </div>
                                            <div className="flex justify-between mt-3 space-x-4">
                                                <Button
                                                    extraSmall={true}
                                                    label={'bk.table.select_date'}
                                                    onClick={() => loadData()}
                                                />
                                                <Button
                                                    extraSmall={true}
                                                    secondary={true}
                                                    label={'bk.table.reset_date'}
                                                    onClick={() => onCalendarReset()}
                                                />
                                            </div>
                                        </>
                                    }
                                />
                            </>
                            : null
                        }
                    </div>
                </div>
                {renderActionButtonsForSelectedRows && selectedRows.length > 0 ?
                    <>
                        {showSelectedActionsDivider ?
                            <div className="border-t border-gray-200 my-4" />
                            : null
                        }
                        <Text label="bk.table.actions" />
                        <div className="flex space-x-4 items-center mb-4 mt-4">
                            {renderActionButtonsForSelectedRows(isLoading)}
                        </div>
                    </>
                    : null
                }
            </div>
        );

        if (isLoading) {
            return (
                <div>
                    {renderHeader()}
                    <div className="flex flex-col bg-white">
                        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                <div className="overflow-hidden border-gray-200">
                                    <Loader />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return(
            <div>
                {renderHeader()}
                <div className="flex flex-col bg-white">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div className="overflow-hidden border-gray-200">
                                <table className="w-full lg:table-fixed">
                                    <thead className="">
                                    <tr>
                                        {onRowSelected ?
                                            <th className="w-10">
                                                <div className="flex -mt-5">
                                                    {selectedRows.length === 0 ||
                                                    selectedRows.length === rowsData.length ?
                                                        <Checkbox
                                                            checked={(selectedRows.length === rowsData.length)}
                                                            onChange={() => {
                                                                if (selectedRows.length === rowsData.length) {
                                                                    setSelectedRows([]);
                                                                    if (onRowSelected) {
                                                                        onRowSelected([]);
                                                                    }
                                                                } else {
                                                                    setSelectedRows(rowsData.map((row, idx) => idx))
                                                                    if (onRowSelected) {
                                                                        onRowSelected(rowsData.map((row, idx) => {
                                                                            if (onRowSelectPassDataField) {
                                                                                return row[onRowSelectPassDataField];
                                                                            }

                                                                            return row;
                                                                        }));
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        :
                                                        <div
                                                            onClick={() => {
                                                                setSelectedRows([]);
                                                                if (onRowSelected) {
                                                                    onRowSelected([]);
                                                                }
                                                            }}
                                                            className="w-5 h-5 bg-bk-p rounded flex items-center justify-center cursor-pointer"
                                                        >
                                                            <div className="w-3 h-[3px] bg-white rounded"></div>
                                                        </div>
                                                    }
                                                </div>
                                            </th>
                                            : null
                                        }
                                        {columns.map((column, idx) => (
                                                <th key={idx} scope="col"
                                                    className={'text-left py-3 pb-8 px-2 text-sm text-bk-grey-100 whitespace-normal break-words ' + (column.width ? column.width : '' ) + (column.type === 'textListByLangs' ? ' pl-5' : '')}>
                                                    <div className="flex items-center ">
                                                        <FormattedMessage id={column.label} />
                                                        {column.tooltip &&
                                                            <Icon type="info" className="mb-2 ml-1" text={column.tooltip} />
                                                        }
                                                    </div>
                                                </th>
                                            )
                                        )}
                                        {/*{actions &&*/}
                                        {/*    <th scope="col" className={'py-3 text-bk-grey-100 text-left text-sm'}>*/}
                                        {/*        <FormattedMessage id={'Actions'}/>*/}
                                        {/*    </th>*/}
                                        {/*}*/}
                                    </tr>
                                    </thead>
                                    <tbody className="">
                                    {rowsData.map((row, idx) => (
                                        <tr key={idx} className="border-dotted border-t">
                                            {onRowSelected ?
                                                <td className="px-2 lg:px-0">
                                                    <div className="flex -mt-4">
                                                        <Checkbox checked={selectedRows.indexOf(idx) !== -1} onChange={() => {
                                                            const itemIdx = selectedRows.indexOf(idx);
                                                            const newSelectedRows = [...selectedRows];

                                                            if (itemIdx !== -1) {
                                                                newSelectedRows.splice(itemIdx, 1);
                                                            } else {
                                                                newSelectedRows.push(idx);
                                                            }

                                                            setSelectedRows(newSelectedRows);

                                                            if (onRowSelected) {
                                                                onRowSelected(newSelectedRows.map((rowIdx) => {
                                                                    if (onRowSelectPassDataField) {
                                                                        return rowsData[rowIdx][onRowSelectPassDataField];
                                                                    }

                                                                    return rowsData[rowIdx];
                                                                }), (onRowSelectPassDataField ? rowsData[idx][onRowSelectPassDataField] : rowsData[idx]));
                                                            }
                                                        }} />
                                                    </div>
                                                </td>
                                                : null
                                            }
                                            {columns.map((column, columnIdx) => {
                                                    if (column.render) {
                                                        return (
                                                            <td key={idx + '_' + columnIdx} className="pt-4 lg:pb-8 py-3 px-2 lg:px-0">
                                                                {column.render(row)}
                                                            </td>
                                                        );
                                                    }

                                                    switch (column.type) {
                                                        case 'rows':
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className="pt-4 lg:pb-8 py-3 px-2 lg:px-0">
                                                                    <div>
                                                                        {row[column.name].map((columnRow: any, columnRowIdx: number) => (
                                                                                <div key={idx + '_' + columnRowIdx}
                                                                                     className={'text-md flex break-words ' + (columnRow.gray ? 'text-gray-500' : 'font-medium text-gray-900')}>
                                                                                    {column.includeIndex ?
                                                                                        <>{(columnRowIdx + 1)}.&nbsp;</>
                                                                                        : null
                                                                                    }
                                                                                    {column.rows?.map((columnRowName, columnRowNameIdx) => (
                                                                                            <div key={idx + '_' + columnRowIdx + '_' + columnRowNameIdx}>
                                                                                                {columnRow[columnRowName]}
                                                                                                {column.actions &&
                                                                                                    <>{column.actions.map((action, idx) => {
                                                                                                            if (action.hideWhen && columnRow[action.hideWhen] === action.hideWhenValue) {
                                                                                                                return null;
                                                                                                            }

                                                                                                            return (
                                                                                                                <a key={idx} className="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500 hover:underline m-1"
                                                                                                                   onClick={() => action.onClick(row, columnRow)}>
                                                                                                                    <FormattedMessage id={action.label}/>
                                                                                                                </a>
                                                                                                            );
                                                                                                        }
                                                                                                    )}
                                                                                                    </>
                                                                                                }
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            );
                                                        case 'status': {
                                                            const blockInfo = column.blockInfoKey ? row[column.blockInfoKey] : null;
                                                            const hasBlockInfo = !!(blockInfo && (blockInfo.blockReason || blockInfo.pinAttempts > 0 || blockInfo.smsAttempts > 0));
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className={"pt-4 lg:pb-8 py-3 whitespace-nowrap px-2 lg:px-0 "}>
                                                                    <div className="flex items-center">
                                                                    {(() => {
                                                                        const statusData = column.statuses && column.statuses[row[column.name]];
                                                                        let statusLabel = '';
                                                                        let statusClass = '';

                                                                        if (Array.isArray(statusData)) {
                                                                            statusLabel = statusData[0] ? intl.formatMessage({ id: statusData[0] }) : '';
                                                                            statusClass = statusData[1];
                                                                        } else {
                                                                            statusLabel = statusData ? intl.formatMessage({ id: statusData}) : '';
                                                                        }

                                                                        if (statusLabel) {
                                                                            return (
                                                                                <span className={`px-2 inline-flex text-md items-center font-semibold rounded-full ${statusClass || 'bg-gray-100'}`}>
                                                                                    <Text label={statusLabel} />
                                                                    </span>
                                                                            );
                                                                        }

                                                                        return null;
                                                                    })()}
                                                                    {hasBlockInfo &&
                                                                        <Icon type="info" className="mb-2 ml-1" text={
                                                                            <div className="flex flex-col gap-1 text-left">
                                                                                {blockInfo.blockReason &&
                                                                                    <span className="font-semibold">{intl.formatMessage({ id: `bk.users.app_block_reason.${blockInfo.blockReason}` })}</span>
                                                                                }
                                                                                <span>{intl.formatMessage({ id: 'bk.users.app_login_attempts.pin' }, { used: blockInfo.pinAttempts, max: blockInfo.pinMaxAttempts })}</span>
                                                                                <span>{intl.formatMessage({ id: 'bk.users.app_login_attempts.sms' }, { used: blockInfo.smsAttempts, max: blockInfo.smsMaxAttempts })}</span>
                                                                            </div>
                                                                        } />
                                                                    }
                                                                    </div>
                                                                </td>
                                                            );
                                                        }
                                                        case 'role':
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className="pt-4 lg:pb-8 py-3 whitespace-nowrap text-md text-gray-500 px-2 lg:px-0">
                                                                    {roles ? <FormattedMessage id={roles[row[column.name]]} /> : null}
                                                                </td>
                                                            );
                                                        case 'date':
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className={"pt-4 lg:pb-8 py-3 whitespace-nowrap px-2 lg:px-0 "}>
                                                                    <div className="text-md font-medium text-gray-900">
                                                                        {formatDate(row[column.name], column.withTime !== undefined ? column.withTime : true)}
                                                                    </div>
                                                                </td>
                                                            );
                                                        case 'number':
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className={"pt-4 lg:pb-8 py-3 whitespace-nowrap px-2 lg:px-0 "}>
                                                                    <div className="text-md font-medium text-gray-900">
                                                                        {formatNumber(row[column.name], true)}
                                                                    </div>
                                                                </td>
                                                            );
                                                        case 'money':
                                                        case 'percents':
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className={"pt-4 lg:pb-8 py-3 whitespace-nowrap px-2 lg:px-0 "}>
                                                                    <div className={'text-md font-medium ' + (row[column.name] < 0 ? 'text-bk-red' : 'text-bk-green')}>
                                                                        {formatNumber(row[column.name], true)} {column.type === 'money' ? `(${currency})` : '%'}
                                                                    </div>
                                                                </td>
                                                            );
                                                        case 'confirmation':
                                                            return (
                                                                <td
                                                                    key={idx + '_' + columnIdx}
                                                                    className={"pt-4 lg:pb-8 py-3 whitespace-nowrap px-2 lg:px-0 "}
                                                                    onClick={() => setConfirmationModalContent(column.descriptionField ? row[column.descriptionField] : null)}
                                                                >
                                                                    {row[column.name] &&
                                                                        <div className="text-sm font-medium text-red-600">
                                                                            <FormattedMessage id="bk.table.column.confirmation.yes" />
                                                                        </div>
                                                                    }
                                                                    {!row[column.name] &&
                                                                        <div className="text-sm font-medium text-green-600">
                                                                            <FormattedMessage id="bk.table.column.confirmation.no" />
                                                                        </div>
                                                                    }
                                                                </td>
                                                            );
                                                        case 'textListByLangs':
                                                          return (
                                                            <td key={idx + '_' + columnIdx} className={(column.className ? column.className + 'pt-4 pb-8 py-3 pl-5 whitespace-nowrap text-md text-gray-900 overflow-hidden' : '')}>
                                                              {Array.isArray(row[column.name]) ?
                                                                row[column.name].map((columnRow: {[key: string]: string}, columnRowIdx: number) => (
                                                                    <div key={idx + '_' + columnIdx + '_' + columnRowIdx}>
                                                                      {columnRow[lang] !== undefined ? columnRow[lang] : ''}
                                                                    </div>
                                                                  )
                                                                ) :
                                                                column.name.indexOf('.') !== -1 ? column.name.split('.').reduce((prev, curr) => (prev ? prev[curr] : null), row) : row[column.name]
                                                              }
                                                            </td>
                                                          );
                                                        case 'receipt':                                   
                                                            return (
                                                                <td key={`${idx}_${columnIdx}`} className="pt-4 pb-8 py-3 whitespace-nowrap text-center">
                                                                    <Icon type="receipt" />
                                                                </td>
                                                            );
                                                        default:
                                                            return (
                                                                <td key={idx + '_' + columnIdx} className={(column.className ? column.className + 'pt-4 lg:pb-8 py-3 whitespace-nowrap text-md font-medium text-gray-900 overflow-hidden px-2 lg:px-0' : 'px-2 lg:px-0')}>
                                                                    <div className="flex items-center ">
                                                                  <p className="break-all">{column.name.indexOf('.') !== -1 ? column.name.split('.').reduce((prev, curr) => (prev ? prev[curr] : null), row) : row[column.name]}</p>
                                                                        {column.tooltipKey && row[column.tooltipKey as string] && row[column.tooltipKey as string] !== '' &&
                                                                            <Icon type="info" className="mb-2 ml-1" text={row[column.tooltipKey as string]} />
                                                                        }
                                                                    </div>
                                                                </td>
                                                            );
                                                    }
                                                }
                                            )}
                                            {actions &&
                                                <td className={"pt-4 pb-8 py-3 whitespace-nowrap text-gray-900 flex justify-end"}>
                                                    {actions.map((action, actionIdx) => {

                                                        if (action.hideWhen && row[action.hideWhen] === action.hideWhenValue) {
                                                            return null;
                                                        }

                                                        if (action.icon) {
                                                            return (
                                                                <div className="ml-1 mr-2 cursor-pointer"
                                                                    onClick={() => action.onClick(row, idx)}>
                                                                    <Icon type={action.icon as 'search' | 'download' | 'api' | 'arrow' | 'pencil' | 'addUser' | 'close' | 'backArrow' | 'info'}
                                                                        className="max-w-none" />
                                                                </div>
                                                            )
                                                        }

                                                        return (
                                                            <Button
                                                                    key={actionIdx}
                                                                extraSmall={true}
                                                                secondary={true}
                                                                    label={
                                                                    action.showSecondLabelWhen && row[action.showSecondLabelWhen] === action.showSecondLabelWhenValue
                                                                        ? action.secondLabel
                                                                        : action.label
                                                                    }
                                                                    color={action.color as "plain" | "grey" | "plainGrey" | "transparent" | "red" | undefined}
                                                                    onClick={() => action.onClick(row, idx)}
                                                                    className="rounded-full ml-2"
                                                            />
                                                        );
                                                    })
                                                    }
                                                </td>
                                            }
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    {pagination ?
                      <div className="py-3 pr-4 mt-4">
                        <span className="text-gray-500 text-sm">{totalRecords} <Text
                          label="bk.placeholder.table.total"/></span>
                        {dataLoader?.apiCall?.totalSumNameInResponse ?
                          <span className="text-gray-500 text-sm ml-12">{formatNumber(totalSum, true)} {currency} <Text
                            label="bk.placeholder.table.totalSum"/></span>
                            : null
                        }
                        <TableNavigation
                            setPage={handleSetPage!!}
                            total={totalRecords!!}
                            page={page.current!!}
                            limit={(limit ? limit : itemsPerPage ? parseInt(itemsPerPage) : (itemsPerPageFromSettings || config.itemsPerPage))}
                            className="float-right"
                        />
                      </div>
                      : null
                    }
                  <Modal isOpen={Boolean(confirmationModalContent)} toggle={() => setConfirmationModalContent(null)}
                           title="bk.table.modal.confirmation.description"
                           content={confirmationModalContent}
                  />
                  {downloadFileInBackgroundCreationModalIsOpen ?
                    <Modal isOpen={true}
                           toggle={handleCloseDownloadFileInBackgroundCreationModal}
                           title={dataLoader?.apiCall?.downloadFileCreationInBackgroundTitle ||
                             'bk.table.modal.download.in_background_file_creation.title'}
                           content={
                             <div>
                               {!isDownloadInProgress && !fileDownloadSuccess ?
                                  <Text justify={true}
                                     label={timeRemainingTillFileReadyForDownload !== null ? dataLoader?.apiCall?.downloadFileCreationInBackgroundDescription : dataLoader?.apiCall?.downloadFileCreationInBackgroundDescriptionNoTimeRemainingAvailable ||
                                       'bk.table.modal.download.in_background_file_creation.description'} {...(downloadInBackgroundLabelValues !== null && (timeRemainingTillFileReadyForDownload !== null || dataLoader?.apiCall?.downloadFileCreationInBackgroundDescriptionNoTimeRemainingAvailable) ? {values: downloadInBackgroundLabelValues} : {})} />
                                 : null
                               }
                               {isDownloadInProgress ?
                                 <Text justify={true} label="bk.table.modal.download.in_background_file_creation.starting_download"></Text>
                                 : null
                               }
                               {fileDownloadSuccess && !isDownloadInProgress ?
                                 <Text justify={true} label="bk.table.modal.download.in_background_file_creation.download_started"></Text>
                                 : null
                               }
                               {fileDownloadFailed ?
                                 <Text error={true} justify={true} label="bk.table.modal.download.in_background_file_creation.failed"></Text>
                                 : null
                               }
                               {timeRemainingTillFileReadyForDownload !== null && !fileDownloadFailed ?
                                 <Text justify={true}
                                       className="mt-2"
                                       label={'bk.table.modal.download.in_background_file_creation.time_remaining'}
                                       values={{time: timeRemainingTillFileReadyForDownload.toString()}}
                                 />
                                 : null
                               }
                               <Button
                                 label="bk.table.modal.download.in_background_file_creation.btn.close"
                                 onClick={handleCloseDownloadFileInBackgroundCreationModal}
                                 noShadow={true}
                                 className="w-full mt-5"
                               />
                             </div>
                           }
                    /> : null
                  }
                </div>
            </div>
        );
    };


type TableNavigationType = {
  total: number,
  page: number,
  limit: number,
  className?: string,
  setPage: (page: number) => void
};

export const TableNavigation: React.FC<TableNavigationType> = ({
                                                                   total,
                                                                   page,
                                                                   limit,
                                                                   className,
                                                                   setPage,
                                                               }) => {
    const lastPage = Math.ceil(total / limit);
    const intl = useIntl();

    const numberBtns = () => {
        const pages = [];
        const isMobile = window.innerWidth < 640;
        const maxVisiblePages = isMobile ? 3 : 6; // Show fewer pages on mobile

        if (lastPage <= maxVisiblePages) {
            for (let i = 1; i <= lastPage; i++) {
                pages.push(i);
            }
        } else {
            if (isMobile) {
                if (page < 2) {
                    pages.push(1, 2, 3, '...');
                } else if (page > lastPage - 1) {
                    pages.push('...', lastPage - 3, lastPage - 2, lastPage - 1, lastPage);
                } else {
                    pages.push('...', page - 1, page, page + 1, '...');
                }
            } else {
                if (lastPage <= maxVisiblePages) {
                    for (let i = 1; i <= lastPage; i++) {
                        pages.push(i);
                    }
                } else {
                    if (page < 5) {
                        pages.push(1, 2, 3, 4, 5, 6, '...');
                    } else if (page > lastPage - 3) {
                        pages.push('...', lastPage - 5, lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage);
                    } else {
                        pages.push('...', page - 1, page, page + 1, '...');
                    }
                }
            }
        }

        return pages.map((p, index) =>
            typeof p === 'number' ? (
                <NavButton
                    key={p}
                    page={p}
                    selected={page === p}
                    label={p.toString()}
                    setPage={setPage}
                    className="px-2 py-1 text-sm"
                />
            ) : (
                <span key={index} className="text-gray-500 px-1">...</span>
            )
        );
    };

    if (lastPage < 2) return null;

    return (
        <div className={`flex flex-wrap items-center space-x-2 space-y-2 sm:space-y-0 ${className || ''}`}>
      <span className="text-xs sm:text-sm text-gray-600">
         <Text label="bk.pagination.page" /> {page} <Text label="bk.pagination.of" /> {lastPage}
      </span>
            {page > 1 && (
                <>
                    <NavButton label={`<< ${intl.formatMessage({id: 'bk.pagination.first'})}`} page={1} setPage={setPage} className="px-2 py-1 text-xs sm:text-sm" />
                    <NavButton label={`< ${intl.formatMessage({id: 'bk.pagination.previous'})}`} page={page - 1} setPage={setPage} className="px-2 py-1 text-xs sm:text-sm" />
                </>
            )}
            {numberBtns()}
            {page < lastPage && (
                <>
                    <NavButton label={`${intl.formatMessage({id: 'bk.pagination.next'})} >`} page={page + 1} setPage={setPage} className="px-2 py-1 text-xs sm:text-sm" />
                    <NavButton label={`${intl.formatMessage({id: 'bk.pagination.last'})} >>`} page={lastPage} setPage={setPage} className="px-2 py-1 text-xs sm:text-sm" />
                </>
            )}
        </div>
    );
};


type NavButtonType = {
    page: number,
    label: string | number,
    setPage: (page: number) => void,
    disabled?: boolean,
    selected?: boolean,
    className?: string
};

export const NavButton: (props: NavButtonType) => JSX.Element =
    ({
         page,
         label,
         setPage,
         disabled,
         selected = false,
         className
     }) => (
        <Button
            onClick={() => setPage(page)}
            disabled={disabled}
            extraSmall={true}
            color={selected ? 'plain' : 'grey'}
            noShadow={true}
            className={className}
        >
            {label}
        </Button>
    );
